import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { TableRow, TableCell } from "components/Table";
import { TermSheetInput } from "components/TermSheets";
import { LEGACY_TERMSHEET_COLUMNS_CONFIG, calculateTotals, getTotalsDisplayConfig } from "constants/index";
import { colors } from "style";
import { useTermSheetContext } from "hooks";

/**
 * TotalsRow
 * Displays totals/summary row at the bottom of the termsheet table
 * Calculates and shows sums and averages for relevant columns
 */
const TotalsRow = () => {
  const { termSheet, changes, conversionMatrix, buyInCurrency, sellInCurrency } = useTermSheetContext();

  const assignments = termSheet?.assignments || [];

  // Calculate totals using the utility function from constants
  const totals = useMemo(
    () =>
      calculateTotals(assignments, changes, {
        conversionMatrix,
        buyInCurrency,
        sellInCurrency,
      }),
    [assignments, changes, conversionMatrix, buyInCurrency, sellInCurrency]
  );

  // Render totals cell based on column configuration
  const renderTotalsCell = (column) => {
    const config = getTotalsDisplayConfig(column, totals, {
      billingCurrency: termSheet?.organizationBillingAccount?.currencyCode,
      buyInCurrency,
      sellInCurrency,
      termSheet,
    });

    if (!config) return null;

    // If config specifies a component, render it directly
    if (config.component) {
      const Component = config.component;
      return <Component {...config.props} />;
    }

    // Otherwise render TermSheetInput with the config
    return (
      <TermSheetInput
        type={config.type}
        value={config.value?.toString()}
        currency={config.currency}
        readOnly={config.readOnly}
        highlightColor={column.highlightColor}
        iconColor={colors.secondary}
        onChange={() => {}} // Provide no-op onChange for read-only fields to prevent errors
      />
    );
  };

  // Don't render if no totals or no assignments
  if (!totals || assignments.length === 0) {
    return null;
  }

  return (
    <StyledTotalsRow>
      {LEGACY_TERMSHEET_COLUMNS_CONFIG.map((column) => (
        <TotalsCell
          key={column.key}
          width={column.width}
          isSticky={column.sticky}
          highlightColor={column.highlightColor}
        >
          {renderTotalsCell(column)}
        </TotalsCell>
      ))}
    </StyledTotalsRow>
  );
};

const StyledTotalsRow = styled(TableRow)`
  background: ${colors.grayAnatomyLight5};
  border-top: 2px solid ${colors.grayAnatomyDark2};
  font-weight: 600;

  &:hover {
    background: ${colors.grayAnatomyLight5};
  }
`;

const TotalsCell = styled(TableCell)`
  padding: 0;
  border-right: 1px solid ${(props) => props.highlightColor || colors.grayAnatomyLight4};
  min-width: ${(props) => props.width}px;
  max-width: ${(props) => props.width}px;
  width: ${(props) => props.width}px;

  ${({ highlightColor }) => highlightColor && `background: ${highlightColor};`}

  ${(props) =>
    props.isSticky &&
    `
    position: sticky;
    left: 0;
    z-index: 4;
    background: ${colors.grayAnatomyLight5};
  `}

  &:last-of-type {
    border-right: none;
    padding-right: 0;
  }
`;

export default TotalsRow;
